import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { storage } from '@/lib/firebase'
import { addFreshPhotoToTattoo, addHealedPhotoToTattoo, deletePhotoFromTattoo } from './clientRegistryData'

/**
 * Upload a fresh tattoo photo (uploaded by artist)
 */
export async function uploadFreshTattooPhoto(
  artistId: string,
  userId: string,
  tattooId: string,
  file: File,
  caption?: string
): Promise<void> {
  try {
    // Validate file
    const maxSize = 10 * 1024 * 1024 // 10MB for tattoo photos
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif']
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif']
    
    if (file.size > maxSize) {
      throw new Error(`File too large. Maximum size: ${maxSize / 1024 / 1024}MB`)
    }
    
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
    const hasValidType = file.type === '' || allowedTypes.includes(file.type)
    const hasValidExtension = allowedExtensions.includes(fileExtension)
    
    if (!hasValidType && !hasValidExtension) {
      throw new Error(`Invalid file type "${file.type}". Please select an image file`)
    }
    
    if (!hasValidExtension) {
      throw new Error(`Invalid file extension "${fileExtension}". Please use: ${allowedExtensions.join(', ')}`)
    }
    
    // Generate unique photo ID and storage path
    const photoId = `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const storagePath = `client-registries/${artistId}/${userId}/tattoos/${tattooId}/fresh/${photoId}${fileExtension}`
    const storageRef = ref(storage, storagePath)
    
    // Upload file
    console.log('Uploading fresh photo to:', storagePath)
    await uploadBytes(storageRef, file)
    
    // Get download URL
    const downloadURL = await getDownloadURL(storageRef)
    
    // Add photo to Firestore
    await addFreshPhotoToTattoo(artistId, userId, tattooId, downloadURL, storagePath, caption)
    
    console.log('Fresh photo uploaded successfully:', downloadURL)
  } catch (error) {
    console.error('Error uploading fresh photo:', error)
    throw new Error(`Failed to upload photo: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Upload a healed tattoo photo (uploaded by client)
 */
export async function uploadHealedTattooPhoto(
  artistId: string,
  userId: string,
  tattooId: string,
  file: File,
  uploadedBy: string,
  caption?: string
): Promise<void> {
  try {
    // Validate file
    const maxSize = 10 * 1024 * 1024 // 10MB for tattoo photos
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif']
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif']
    
    if (file.size > maxSize) {
      throw new Error(`File too large. Maximum size: ${maxSize / 1024 / 1024}MB`)
    }
    
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
    const hasValidType = file.type === '' || allowedTypes.includes(file.type)
    const hasValidExtension = allowedExtensions.includes(fileExtension)
    
    if (!hasValidType && !hasValidExtension) {
      throw new Error(`Invalid file type "${file.type}". Please select an image file`)
    }
    
    if (!hasValidExtension) {
      throw new Error(`Invalid file extension "${fileExtension}". Please use: ${allowedExtensions.join(', ')}`)
    }
    
    // Generate unique photo ID and storage path
    const photoId = `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const storagePath = `client-registries/${artistId}/${userId}/tattoos/${tattooId}/healed/${photoId}${fileExtension}`
    const storageRef = ref(storage, storagePath)
    
    // Upload file
    console.log('Uploading healed photo to:', storagePath)
    await uploadBytes(storageRef, file)
    
    // Get download URL
    const downloadURL = await getDownloadURL(storageRef)
    
    // Add photo to Firestore
    await addHealedPhotoToTattoo(artistId, userId, tattooId, downloadURL, storagePath, uploadedBy, caption)
    
    console.log('Healed photo uploaded successfully:', downloadURL)
  } catch (error) {
    console.error('Error uploading healed photo:', error)
    throw new Error(`Failed to upload photo: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Delete a tattoo photo from both Storage and Firestore
 */
export async function deleteTattooPhoto(
  artistId: string,
  userId: string,
  tattooId: string,
  photoId: string,
  storagePath: string,
  photoType: 'fresh' | 'healed'
): Promise<void> {
  try {
    // Delete from Storage
    const storageRef = ref(storage, storagePath)
    await deleteObject(storageRef)
    
    // Delete from Firestore
    await deletePhotoFromTattoo(artistId, userId, tattooId, photoId, photoType)
    
    console.log('Photo deleted successfully:', photoId)
  } catch (error) {
    console.error('Error deleting photo:', error)
    throw new Error(`Failed to delete photo: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}


